import { DocumentaryEvidenceHelper } from '@app/my-zone/shared/paperwork/documentary-evidence/documentary-evidence.helper';
import * as fromRoot from '@app/store/app.reducer';
import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import { DocumentaryEvidenceInterface } from '../../../../core/interfaces/documentary-evidence.interface';
import * as fromAppointments from './reducers/appointments.reducer';
import * as fromClaims from './reducers/claims.reducer';
import * as fromCorrespondences from './reducers/correspondences.reducer';
import * as fromDocuments from './reducers/documents.reducer';
import * as fromInvolvedParties from './reducers/involvedParties.reducer';
import * as fromPhotos from './reducers/photos.reducer';

// TODO: move store to /verzekeringsmap/store/
export interface State extends fromRoot.State {
    myClaims: MyClaimsState;
}

export interface State {
    claims: fromClaims.State;
    correspondences: fromCorrespondences.State;
    documents: fromDocuments.State;
    appointments: fromAppointments.State;
    photos: fromPhotos.State;
    involvedParties: fromInvolvedParties.State;
}

export interface MyClaimsState {
    claims: fromClaims.State;
    correspondences: fromCorrespondences.State;
    documents: fromDocuments.State;
    appointments: fromAppointments.State;
    photos: fromPhotos.State;
    involvedParties: fromInvolvedParties.State;
}

export const reducers: ActionReducerMap<MyClaimsState, any> = {
    claims: fromClaims.reducer,
    correspondences: fromCorrespondences.reducer,
    documents: fromDocuments.reducer,
    appointments: fromAppointments.reducer,
    photos: fromPhotos.reducer,
    involvedParties: fromInvolvedParties.reducer,
};

/**
 *  Claim selectors
 */
export const getMyClaimsState = createFeatureSelector< MyClaimsState>('myClaims');

export const getClaimsState = createSelector(getMyClaimsState, (state) => state.claims);
export const getClaimIds = createSelector(getClaimsState, fromClaims.getIds);
export const getSelectedClaimId = createSelector(getClaimsState, fromClaims.getSelectedId);
export const getClaimEntities = createSelector(getClaimsState, fromClaims.getEntities);
export const getProcessSteps = createSelector(getClaimsState, fromClaims.getProcessSteps);
export const getActions = createSelector(getClaimsState, fromClaims.getActions);
export const isClaimLoaded = createSelector(getClaimsState, fromClaims.isLoaded);

export const getClaimCollection = createSelector(getClaimEntities, getClaimIds, (entities, ids) => {
    return ids.map((id) => entities[id]);
});

export const getProcessStepsCollection = createSelector(getProcessSteps, getClaimIds, (entities, ids) => {
    return ids.map((id) => entities[id]);
});

export const getActionsCollection = createSelector(getActions, getClaimIds, (entities, ids) => {
    return ids.map((id) => entities[id]);
});

export const getSelectedClaim = createSelector(getClaimEntities, getSelectedClaimId, (entities, selectedId) => {
    return entities[selectedId];
});

export const getSelectedProcessSteps = createSelector(getProcessSteps, getSelectedClaimId, (processSteps, selectedId) => {
    return processSteps[selectedId];
});

export const getSelectedActions = createSelector(getActions, getSelectedClaimId, (actions, selectedId) => {
    return actions[selectedId];
});

export const getUnfinishedClaims = createSelector(getClaimCollection, (claims) => {
    return claims.filter((claim) => {
        const status = (claim.dossier_indicatoren) ? claim.dossier_indicatoren.pg_status : claim.pg_status;
        return ['Hangend'].includes(status);
    });
});

export const getOpenClaims = createSelector(getClaimCollection, (claims) => {
    return claims.filter((claim) => {
        const status = (claim.dossier_indicatoren) ? claim.dossier_indicatoren.pg_status : claim.pg_status;
        return ['Open'].includes(status);
    });
});

export const getCompletedClaims = createSelector(getClaimCollection, (claims) => {
    return claims.filter((claim) => {
        const status = (claim.dossier_indicatoren) ? claim.dossier_indicatoren.pg_status : claim.pg_status;
        return !['Open', 'Hangend'].includes(status);
    });
});

/**
 *  Correspondences selectors
 */
export const getCorrespondenceState = createSelector(getMyClaimsState, (state) => state.correspondences);

export const getCorrespondenceIds = createSelector(getCorrespondenceState, fromCorrespondences.getIds);
export const getCorrespondenceEntities = createSelector(getCorrespondenceState, fromCorrespondences.getEntities);
export const getCorrespondencesLoaded = createSelector(getCorrespondenceState, fromCorrespondences.getLoaded);

export const getSelectedCorrespondence = createSelector(getCorrespondenceEntities, getSelectedClaimId, (entities, selectedId) => {
    return entities[selectedId];
});

export const isCorrespondencesLoaded = createSelector(getCorrespondencesLoaded, getSelectedClaimId, (loaded, selectedId) => {
    return loaded[selectedId];
});

/**
 *  Documents selectors
 */
export const getDocumentsState = createSelector(getMyClaimsState, (state) => state.documents);

export const getBewijsstukIds = createSelector(getDocumentsState, fromDocuments.getIds);
export const getBewijsstukEntities = createSelector(getDocumentsState, fromDocuments.getEntities);
export const getBewijsstukCollection = createSelector(getBewijsstukEntities, getBewijsstukIds, (entities, ids) => {
    return ids.map((id) => entities[id]);
});

const createExtraDocumentBewijsstuk = (dossier_volgnummer: number, relatie_id: number) => {
    return DocumentaryEvidenceHelper.CreateDocumentaryEvidence({
        document_id: 'EXDO',
        document_omschrijving: 'Extra geupload document',
        dossier_volgnummer: dossier_volgnummer,
        knoppen: [{
            knop_naam: 'uploaden-bewijsstuk',
            knop_status: 'zichtbaar',
        }],
        organisatie_id: 'INSH',
        organisatie_omschrijving: 'InShared',
        pg_bewijsstuk: 'Document',
        pg_omschrijving: 'Extra geupload document',
        pg_status_code: 'WAON',
        pg_status_code_omschrijving: 'Wacht op ontvangst',
        relatie_id: relatie_id,
        soort_bewijsstuk_code: 'DOCU',
        soort_bewijsstuk_omschrijving: 'Document',
        bewijsstuk_volgnummer: 99,
    });
};

export const getSelectedBewijsstuk = createSelector(getBewijsstukEntities, getSelectedClaimId, (entities, selectedId) => {
    if (!entities[selectedId] || !entities[selectedId].bewijsstuk) {
        return;
    }

    const bewijsstuk: DocumentaryEvidenceInterface[] = [...entities[selectedId].bewijsstuk];
    const knoppen = entities[selectedId].knoppen;

    if (!bewijsstuk) {
        return;
    }

    // Check to include Extra document
    const extraDocument = bewijsstuk.some((bewijsstukExtraDocument) => bewijsstukExtraDocument.document_id === 'EXDO');
    const uploadOther = knoppen.some((knop) => {
        return knop.knop_naam === 'uploaden_overig' && knop.knop_status === 'zichtbaar';
    });

    if (!extraDocument && uploadOther && bewijsstuk[0]) {
        bewijsstuk.push(
            createExtraDocumentBewijsstuk(bewijsstuk[0].dossier_volgnummer, bewijsstuk[0].relatie_id),
        );
    }

    return bewijsstuk;
});
export const getSelectedBewijsstukken = createSelector(getBewijsstukEntities, getSelectedClaimId, (entities, selectedId) => {
    if (!entities[selectedId]) {
        return;
    }
    return entities[selectedId];
});

export const getSelectedKnoppen = createSelector(getBewijsstukEntities, getSelectedClaimId, (entities, selectedId) => {
    if (!entities[selectedId]) {
        return;
    }
    return entities[selectedId].knoppen;
});
export const getSelectedUploadEnveloppeVolgnummer = createSelector(getBewijsstukEntities, getSelectedClaimId, (entities, selectedId) => {
    if (!entities[selectedId]) {
        return;
    }
    return entities[selectedId].indienen.upload_enveloppe_volgnummer;
});

export const isDocumentsLoading = createSelector(getDocumentsState, fromDocuments.isLoading);
export const getBewijsstukkenLoaded = createSelector(getDocumentsState, fromDocuments.getLoaded);
export const isSelectedBewijsstukkenLoaded = createSelector(getBewijsstukkenLoaded, getSelectedClaimId, (loaded, selectedId) => {
    return loaded[selectedId];
});

export const getBewijsstukkenMessages = createSelector(getDocumentsState, fromDocuments.getMessages);
export const getSelectedBewijsstukMessages = createSelector(getBewijsstukkenMessages, getSelectedClaimId, (messages, selectedId) => {
    if (!messages[selectedId]) {
        return;
    }
    return messages[selectedId];
});

export const getLastUpload = createSelector(getDocumentsState, fromDocuments.getLastUpload);

/**
 *  Appointments selectors
 */
export const getAppointmentsState = createSelector(getMyClaimsState, (state) => state.appointments);
export const getAppointmentsEntities = createSelector(getAppointmentsState, fromAppointments.getEntities);
export const getAppointmentsLoaded = createSelector(getAppointmentsState, fromAppointments.getLoaded);
export const getAppointmentErrors = createSelector(getAppointmentsState, fromAppointments.getErrors);

export const getSelectedAppointment = createSelector(getAppointmentsEntities, getSelectedClaimId, (entities, selectedId) => {
    return entities[selectedId];
});

export const getSelectedAppointmentErrors = createSelector(getAppointmentErrors, getSelectedClaimId, (errors, selectedId) => {
    return errors[selectedId];
});

export const isSelectedAppointmentsLoaded = createSelector(getAppointmentsLoaded, getSelectedClaimId, (loaded, selectedId) => {
    return loaded[selectedId];
});

export const isAppointmentsLoading = createSelector(getAppointmentsState, fromAppointments.isLoading);

/**
 *  Photos selectors
 */
export const getPhotoState = createSelector(getMyClaimsState, (state) => state.photos);

export const getPhotoIds = createSelector(getPhotoState, fromPhotos.getIds);
export const getPhotoEntities = createSelector(getPhotoState, fromPhotos.getEntities);
export const getPhotosLoaded = createSelector(getPhotoState, fromPhotos.getLoaded);

export const getPhotoCollection = createSelector(getPhotoEntities, getPhotoIds, (entities, ids) => {
    return ids.map((id) => entities[id]);
});

export const isPhotosLoaded = createSelector(getPhotosLoaded, getSelectedClaimId, (loaded, selectedId) => {
    return loaded[selectedId];
});

export const getSelectedPhotoCollection = createSelector(getPhotoEntities, getSelectedClaimId, (entities, selectedId) => {
    return (entities[selectedId]) ? entities[selectedId] : [];
});

/**
 *  Involved Parties selectors
 */
export const getInvolvedPartiesState = createSelector(getMyClaimsState, (state) => state.involvedParties);

export const getInvolvedPartiesIds = createSelector(getInvolvedPartiesState, fromInvolvedParties.getIds);
export const getInvolvedPartiesEntities = createSelector(getInvolvedPartiesState, fromInvolvedParties.getEntities);
export const getInvolvedPartiesLoaded = createSelector(getInvolvedPartiesState, fromInvolvedParties.getLoaded);
export const getInvolvedPartiesErrors = createSelector(getInvolvedPartiesState, fromInvolvedParties.getErrors);

export const getSelectedInvolvedParties = createSelector(getInvolvedPartiesEntities, getSelectedClaimId, (entities, selectedId) => {
    return entities[selectedId];
});

export const getSelectedInvolvedPartiesErrors = createSelector(getInvolvedPartiesErrors, getSelectedClaimId, (errors, selectedId) => {
    return errors[selectedId];
});

export const isSelectedInvolvedPartiesLoaded = createSelector(getInvolvedPartiesLoaded, getSelectedClaimId, (loaded, selectedId) => {
    return loaded[selectedId];
});

export const isInvolvedPartiesLoading = createSelector(getInvolvedPartiesState, fromInvolvedParties.isLoading);
